import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@clerk/clerk-react';
import { Bot, X, Send } from 'lucide-react';
import { Badge } from './ds';

const FONT = "'IBM Plex Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AiAssistant = ({ open, onClose, currentQuestion, assessmentId }) => {
  const [messages, setMessages] = useState([
    { role: 'assistant', text: 'Hallo! Ich bin Ihr Audicio-Assistent. Stellen Sie mir gerne Fragen zur aktuellen Anforderung – ich erkläre sie verständlich.' }
  ]);
  const [input, setInput]     = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef             = useRef(null);
  const inputRef              = useRef(null);
  const { getToken }          = useAuth();

  useEffect(() => {
    if (open) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setMessages(prev => [...prev, { role: 'user', text }]);
    setInput('');
    setLoading(true);

    try {
      const token = await getToken();
      const res = await axios.post(
        `${API_URL}/ai/chat`,
        { message: text, question: currentQuestion, assessmentId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(prev => [...prev, { role: 'assistant', text: res.data.reply }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Entschuldigung, ich bin gerade nicht erreichbar. Bitte versuchen Sie es später.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 800,
          background: 'rgba(10,14,23,0.45)',
          backdropFilter: 'blur(2px)',
        }}
      />

      {/* Chat panel — centered */}
      <div style={{
        position: 'fixed',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 900,
        width: 420, maxWidth: '90vw',
        maxHeight: 'min(80vh, calc(var(--vh-fill) - 48px))',
        background: '#fff', borderRadius: 16,
        border: '1px solid #D7DCE4',
        boxShadow: '0 12px 28px rgba(16,22,35,0.10), 0 4px 10px rgba(16,22,35,0.05)',
        display: 'flex', flexDirection: 'column',
        fontFamily: FONT, overflow: 'hidden',
      }}>

        {/* Header */}
        <div style={{
          background: '#1E4FBE',
          padding: '16px 20px',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 38, height: 38, borderRadius: '50%',
            background: 'rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Bot width={19} height={19} color="#fff" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>Audicio Assistent</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>Fragen zur aktuellen Anforderung</div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.15)', border: 'none', cursor: 'pointer',
              color: '#fff', width: 28, height: 28, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <X width={14} height={14} />
          </button>
        </div>

        {/* Context pill */}
        {currentQuestion && (
          <div style={{
            padding: '8px 16px', background: '#F5F7FA',
            borderBottom: '1px solid #D7DCE4',
            fontSize: 11, color: '#667085',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Badge tone="info">Nr. {currentQuestion.dinNr}</Badge>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {currentQuestion.text}
            </span>
          </div>
        )}

        {/* Messages */}
        <div style={{
          flex: 1, overflowY: 'auto', padding: '16px 16px 8px',
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          {messages.map((msg, i) => (
            <div key={i} style={{
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            }}>
              <div style={{
                maxWidth: '85%', padding: '10px 14px', borderRadius: 14,
                background: msg.role === 'user' ? '#1E4FBE' : '#EBEEF3',
                color: msg.role === 'user' ? '#fff' : '#1B2333',
                fontSize: 13, lineHeight: 1.6,
                borderBottomRightRadius: msg.role === 'user' ? 4 : 14,
                borderBottomLeftRadius: msg.role === 'assistant' ? 4 : 14,
              }}>
                {msg.text}
              </div>
            </div>
          ))}

          {loading && (
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <div style={{
                padding: '10px 16px', borderRadius: 14, borderBottomLeftRadius: 4,
                background: '#EBEEF3', fontSize: 18, color: '#8A93A3', letterSpacing: 3,
              }}>●●●</div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div style={{
          padding: '12px 14px', borderTop: '1px solid #D7DCE4',
          display: 'flex', gap: 8, alignItems: 'flex-end',
        }}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Frage stellen… (Enter zum Senden)"
            rows={1}
            style={{
              flex: 1, resize: 'none', border: '1px solid #D7DCE4',
              borderRadius: 8, padding: '9px 12px', fontSize: 16,
              fontFamily: FONT, outline: 'none', color: '#101623',
              lineHeight: 1.5, maxHeight: 80, overflowY: 'auto',
            }}
          />
          <button
            onClick={send}
            disabled={!input.trim() || loading}
            style={{
              width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
              background: input.trim() && !loading ? '#1E4FBE' : '#EBEEF3',
              border: 'none', cursor: input.trim() && !loading ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 120ms',
            }}
          >
            <Send width={15} height={15} color={input.trim() && !loading ? '#fff' : '#8A93A3'} strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </>
  );
};

export default AiAssistant;
